import React, { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableRecruitmentItem from "./SortableRecruitmentItem";

// Styles pour la scrollbar personnalisée
const scrollbarStyles = `
  .recruitment-container-scroll::-webkit-scrollbar {
    width: 6px;
  }
  .recruitment-container-scroll::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .recruitment-container-scroll::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .recruitment-container-scroll::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

// Injecter les styles si pas déjà fait
if (typeof document !== 'undefined' && !document.getElementById('recruitment-container-scrollbar-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'recruitment-container-scrollbar-styles';
  styleSheet.textContent = scrollbarStyles;
  document.head.appendChild(styleSheet);
}

const baseStyle = {
  padding: 10,
  flex: 1,
  minWidth: 250,
  maxHeight: "calc(100vh - 250px)", // Hauteur maximale basée sur la hauteur de l'écran
  borderRadius: 8,
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  display: "flex",
  flexDirection: "column",
};

export default function RecruitmentContainer({
  id,
  title,
  items,
  allItems,
  selectedCandidates,
  onSelectCandidate,
  onSelectColumn,
  onNavigateToIndividual,
  filters,
  onFilterChange
}) {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const { setNodeRef, isOver } = useDroppable({
    id,
    data: {
      type: 'container',
      droppableId: id,
    }
  });

  const containerStyle = {
    ...baseStyle,
    backgroundColor: isOver ? "#e0f2fe" : "#ffffff",
    transform: isOver ? "scale(1.02)" : "scale(1)",
    transition: "all 0.2s ease",
    borderColor: isOver ? "#0284c7" : "#e2e8f0",
  };

  const headerStyle = {
    margin: "0 0 15px 0", 
    padding: "8px",
    fontSize: "14px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#475569",
    borderBottom: "2px solid #e2e8f0",
    backgroundColor: "#f8fafc",
    borderRadius: "6px"
  };

  const columnCandidateIds = items.map(item => item._id);
  const areAllSelected = columnCandidateIds.length > 0 && columnCandidateIds.every(id => selectedCandidates.includes(id));
  const isSomeSelected = columnCandidateIds.some(id => selectedCandidates.includes(id));

  const handleColumnSelect = () => {
    if (onSelectColumn) {
      onSelectColumn(id, columnCandidateIds, !areAllSelected);
    }
  };

  const handleFilterToggle = (filterType) => {
    const currentFilters = filters || {};
    const newFilters = {
      ...currentFilters,
      [filterType]: !currentFilters[filterType]
    };

    // Si tous les filtres sont désactivés, on passe undefined
    if (!newFilters.hasProEmail && !newFilters.hasPersoEmail && !newFilters.hasPhone) {
      onFilterChange(undefined);
    } else {
      onFilterChange(newFilters);
    }
  };

  const activeFiltersCount = filters
    ? (filters.hasProEmail ? 1 : 0) + (filters.hasPersoEmail ? 1 : 0) + (filters.hasPhone ? 1 : 0)
    : 0;

  // Fonction pour obtenir la couleur selon l'étape
  const getStageColor = (stage) => {
    const colors = {
      'shortlist': '#64748b',
      'contacté': '#3b82f6',
      'appel': '#8b5cf6',
      'qualif': '#06b6d4',
      'cv envoyé': '#10b981',
      'entretien 1': '#f59e0b',
      'entretien 2': '#f97316',
      'entretien 3': '#ef4444',
      'offre': '#84cc16',
      'ok': '#22c55e',
      'ko': '#ef4444',
    };
    return colors[stage] || '#64748b';
  };

  return (
    <SortableContext
      id={id}
      items={items.map((candidate) => candidate._id)}
      strategy={verticalListSortingStrategy}
    >
      <div ref={setNodeRef} style={containerStyle} role="region" aria-label={`Stage ${id}`}>
        <div style={headerStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={areAllSelected}
                ref={input => {
                  if (input) input.indeterminate = isSomeSelected && !areAllSelected;
                }}
                onChange={handleColumnSelect}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                title={areAllSelected ? "Désélectionner tous" : "Sélectionner tous"}
              />
              <span style={{ textAlign: "center", flex: 1 }}>
                {title || id}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* Bouton de filtre */}
              <button
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                style={{
                  backgroundColor: activeFiltersCount > 0 ? "#4f46e5" : "transparent",
                  color: activeFiltersCount > 0 ? "white" : "#64748b",
                  border: activeFiltersCount > 0 ? "none" : "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "4px 8px",
                  fontSize: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  transition: "all 0.2s ease"
                }}
                title="Filtrer les profils"
              >
                <i className="bi bi-funnel" style={{ fontSize: "10px" }}></i>
                {activeFiltersCount > 0 && (
                  <span style={{
                    backgroundColor: "white",
                    color: "#4f46e5",
                    borderRadius: "50%",
                    width: "16px",
                    height: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: "600"
                  }}>
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              {/* Compteur */}
              <span style={{
                backgroundColor: getStageColor(id),
                color: "white",
                borderRadius: "12px",
                padding: "2px 8px",
                fontSize: "12px",
                fontWeight: "normal"
              }}>
                {items.length}{allItems && items.length !== allItems.length ? `/${allItems.length}` : ''}
              </span>
            </div>
          </div>

          {/* Panneau de filtres */}
          {isFilterPanelOpen && (
            <div style={{
              marginTop: "12px",
              padding: "12px",
              backgroundColor: "#f8fafc",
              borderRadius: "6px",
              border: "1px solid #e2e8f0"
            }}>
              <div style={{ marginBottom: "8px", fontSize: "11px", fontWeight: "600", color: "#475569", textTransform: "none" }}>
                Afficher uniquement les profils avec :
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#475569",
                  textTransform: "none",
                  fontWeight: "normal"
                }}>
                  <input
                    type="checkbox"
                    checked={filters?.hasProEmail || false}
                    onChange={() => handleFilterToggle('hasProEmail')}
                    className="w-3.5 h-3.5 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  Email professionnel
                </label>
                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#475569",
                  textTransform: "none",
                  fontWeight: "normal"
                }}>
                  <input
                    type="checkbox"
                    checked={filters?.hasPersoEmail || false}
                    onChange={() => handleFilterToggle('hasPersoEmail')}
                    className="w-3.5 h-3.5 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  Email personnel
                </label>
                <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                  fontSize: "12px",
                  color: "#475569",
                  textTransform: "none",
                  fontWeight: "normal"
                }}>
                  <input
                    type="checkbox"
                    checked={filters?.hasPhone || false}
                    onChange={() => handleFilterToggle('hasPhone')}
                    className="w-3.5 h-3.5 text-indigo-600 bg-white border-gray-300 rounded focus:ring-indigo-500 focus:ring-2"
                  />
                  Numéro de téléphone
                </label>
              </div>
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => onFilterChange(undefined)}
                  style={{
                    marginTop: "10px",
                    width: "100%",
                    padding: "6px",
                    backgroundColor: "white",
                    border: "1px solid #cbd5e1",
                    borderRadius: "4px",
                    fontSize: "11px",
                    color: "#64748b",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    textTransform: "none",
                    fontWeight: "normal"
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = "#f1f5f9";
                    e.target.style.borderColor = "#94a3b8";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "white";
                    e.target.style.borderColor = "#cbd5e1";
                  }}
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          )}
        </div>
        <div
          className="recruitment-container-scroll"
          style={{
            flex: 1,
            minHeight: "200px",
            position: "relative",
            overflowY: "auto",
            overflowX: "hidden",
            paddingRight: "2px"
          }}>
          {items.length === 0 ? (
            <div style={{
              textAlign: "center",
              color: "#94a3b8",
              padding: "20px",
              backgroundColor: "#f8fafc",
              borderRadius: "6px",
              border: "2px dashed #cbd5e1",
              fontSize: "14px",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              Aucun candidat
            </div>
          ) : (
            <>
              {items.map((candidate) => (
                <SortableRecruitmentItem
                  key={candidate._id}
                  id={candidate._id}
                  candidate={candidate}
                  containerId={id}
                  selected={selectedCandidates.includes(candidate._id)}
                  onSelect={onSelectCandidate}
                  onNavigate={onNavigateToIndividual}
                />
              ))}

              {/* Carte placeholder invisible pour améliorer le drop */}
              <div
                key={`${id}-placeholder`}
                style={{
                  height: "30px",
                  marginBottom: "8px",
                  borderRadius: "6px",
                  border: isOver ? "2px dashed #0284c7" : "2px dashed transparent",
                  backgroundColor: isOver ? "rgba(2, 132, 199, 0.05)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isOver ? "#0284c7" : "transparent",
                  fontSize: "11px",
                  fontWeight: "500",
                  transition: "all 0.2s ease"
                }}
              >
                {isOver ? "Déposer ici" : ""}
              </div>

              {/* Zone de drop flexible qui s'étend */}
              <div style={{
                flex: 1,
                minHeight: "20px",
                opacity: isOver ? 0.05 : 0,
                backgroundColor: isOver ? "#0284c7" : "transparent",
                borderRadius: "6px"
              }} />
            </>
          )}
        </div>
      </div>
    </SortableContext>
  );
}